import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Custom element wrapping the Truemed HSA/FSA badge on the PDP. Reads the
 * per-variant eligibility map from `data-truemed-variants` and toggles the
 * inner `.truemed-badge-container` on `variant:update` events scoped to its
 * own product.
 *
 * @extends {Component}
 */
export class TruemedWidget extends Component {
  /** @type {((event: Event) => void) | null} */
  _onVariantUpdate = null;

  connectedCallback() {
    super.connectedCallback();

    this._onVariantUpdate = this.handleVariantUpdate.bind(this);
    document.addEventListener(ThemeEvents.variantUpdate, this._onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    if (this._onVariantUpdate) {
      document.removeEventListener(ThemeEvents.variantUpdate, this._onVariantUpdate);
      this._onVariantUpdate = null;
    }
  }

  /**
   * @param {Event} event
   */
  handleVariantUpdate(event) {
    const detail = /** @type {CustomEvent} */ (event).detail;

    const productId = detail?.data?.productId;
    if (productId && String(productId) !== this.dataset.productId) return;

    const variantId = detail?.resource?.id;
    if (!variantId) return;

    const container = this.querySelector('.truemed-badge-container');
    if (!(container instanceof HTMLElement)) return;

    const status = this.readVariantStatuses()[String(variantId)];
    const isEligible = status === 'eligible' || status === 'eligible_with_lmn';
    container.style.display = isEligible ? 'block' : 'none';
  }

  /**
   * @returns {Record<string, string | null>}
   */
  readVariantStatuses() {
    try {
      return JSON.parse(this.dataset.truemedVariants || '{}');
    } catch (_err) {
      return {};
    }
  }
}

if (!customElements.get('truemed-widget')) {
  customElements.define('truemed-widget', TruemedWidget);
}
